import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AttestationRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetPolicyAttestationRecordsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC action data to match the GraphQL query structure
 */
export function mapTrpcAttestationRecordsToGraphQL(
  trpcData: AttestationRegisterResponse | undefined
): GetPolicyAttestationRecordsQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    attestation_record: trpcData.attestation_record,
  };
}

export const useGetAttestationsRegister = (userId?: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.attestation.register.queryOptions({ userId }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcAttestationRecordsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: trpcRefetch,
  };
};
